import { useEffect } from "react";

import { Box } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useEntity from "../../hooks/useEntity";

import Input from "../elements/Input";
import Select from "../elements/Select";
import BannerSelect from "../ui/BannerSelect";
import AddressEditor from "./AddressEditor";
import EditorToolbar from "./EditorToolbar";

const LocationEditor = ({ locationId, onSave }) => {
  const { getLabel } = useLocalization();
  const {
    setEntity,
    emptyObject,
    entityData,
    clearEntityData,
    getEntityData,
    validateEntityData,
    updateField,
    message,
    isSuccessful,
    getFieldMessage,
    saved,
  } = useEntity();

  useEffect(() => {
    setEntity("Location");
  }, []);

  useEffect(() => {
    if (!locationId) {
      clearEntityData();
    } else getEntityData(locationId);
  }, [locationId, emptyObject]);

  const handleSave = async () => {
    validateEntityData();
  };

  useEffect(() => {
    if (!saved) return;
    onSave();
  }, [saved]);

  const updateAddressField = (fieldName, rawValue) => {
    const currentAddress =
      entityData && typeof entityData.address === "object" && entityData.address
        ? entityData.address
        : {};

    let nextValue = rawValue;
    if (fieldName === "latitude" || fieldName === "longitude") {
      const trimmed = `${rawValue ?? ""}`.trim();
      if (trimmed === "") {
        nextValue = null;
      } else {
        const parsed = Number(trimmed);
        nextValue = Number.isFinite(parsed) ? parsed : null;
      }
    }

    updateField("address", { ...currentAddress, [fieldName]: nextValue });
  };

  if (!entityData) return;

  return (
    <Box
      width="100%"
      height="100%"
    >
      <EditorToolbar
        onSave={handleSave}
        message={message}
      />
      <Box
        width="calc(100% - 2rem)"
        height="calc(100% - 2rem - 30px)"
        padding="1rem"
        display="flex"
        flexDirection="column"
        gap={1}
        overflow="auto"
      >
        <Input
          id="locationName"
          label={getLabel("name")}
          value={entityData.name}
          onChange={(newValue) => updateField("name", newValue)}
          required
          fullwidth
          {...getFieldMessage("name")}
        />

        <Box
          display="flex"
          gap={1}
        >
          <BannerSelect
            value={entityData.bannerId}
            onChange={(newValue) => updateField("bannerId", newValue)}
          />

          <Input
            id="locationNumber"
            label={getLabel("location_number")}
            value={entityData.locationNumber}
            onChange={(newValue) => updateField("locationNumber", newValue)}
            fullwidth
            {...getFieldMessage("locationNumber")}
          />
        </Box>

        <AddressEditor
          title={getLabel("address")}
          value={entityData?.address}
          onChange={(newValue) => updateField("address", newValue)}
        />

        <Box
          display="flex"
          gap={1}
        >
          <Input
            id="locationLatitude"
            label="latitude"
            type="number"
            value={entityData?.address?.latitude ?? ""}
            onChange={(newValue) => updateAddressField("latitude", newValue)}
            fullwidth
          />

          <Input
            id="locationLongitude"
            label="longitude"
            type="number"
            value={entityData?.address?.longitude ?? ""}
            onChange={(newValue) => updateAddressField("longitude", newValue)}
            fullwidth
          />
        </Box>

        <Box
          display="flex"
          gap={1}
        >
          <Select
            id="locationStatus"
            label={getLabel("status")}
            value={entityData.status || "open"}
            onChange={(newValue) => updateField("status", newValue)}
            items={[
              { id: "open", name: "Open" },
              { id: "closed", name: "Closed" },
            ]}
          />

          <Input
            id="locationFormat"
            label={getLabel("format")}
            value={entityData.format}
            onChange={(newValue) => updateField("format", newValue)}
            fullwidth
            {...getFieldMessage("format")}
          />
        </Box>

        <Input
          id="locationNotes"
          label="notes"
          value={entityData.notes}
          onChange={(newValue) => updateField("notes", newValue)}
          multiline
          fullwidth
        />
      </Box>
    </Box>
  );
};

export default LocationEditor;
